import { Mic, MicOff, Phone, PhoneOff, Video, VideoOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Avatar } from '@/components/Avatar';
import { useCall } from './CallProvider';
import { useCallStore } from './useCallSession';

function formatDuration(startedAt: number | null): string {
  if (!startedAt) return '';
  const seconds = Math.floor((Date.now() - startedAt) / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * The call surface.
 *
 * Mounted once for the whole session so a call survives navigating between
 * chats and settings. It renders nothing at all while idle, which is most of
 * the time.
 */
export function CallHost() {
  const phase = useCallStore((state) => state.phase);
  const peerName = useCallStore((state) => state.peerName);
  const mediaKind = useCallStore((state) => state.mediaKind);
  const micEnabled = useCallStore((state) => state.micEnabled);
  const cameraEnabled = useCallStore((state) => state.cameraEnabled);
  const startedAt = useCallStore((state) => state.startedAt);
  const localStream = useCallStore((state) => state.localStream);
  const remoteStream = useCallStore((state) => state.remoteStream);
  const session = useCall();

  const remoteVideo = useRef<HTMLVideoElement>(null);
  const localVideo = useRef<HTMLVideoElement>(null);
  const remoteAudio = useRef<HTMLAudioElement>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (phase !== 'active') return;
    const timer = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [phase]);

  // The shared session describes a stream by the members a call needs, not as
  // the browser's `MediaStream`; in this client it always is one.
  const remote = remoteStream as MediaStream | null;
  const local = localStream as MediaStream | null;

  useEffect(() => {
    if (remoteVideo.current) {
      remoteVideo.current.srcObject = remote;
      // The <audio> element below is the only sink for the other side's voice.
      // A <video> element plays the audio track of its stream too, so leaving
      // this one unmuted rendered the same voice twice: the two playbacks
      // drift apart by tens of milliseconds, which is heard as an echo, and
      // the doubled output is loud enough for the microphone to catch it and
      // feed it back until the call howls.
      remoteVideo.current.muted = true;
    }
    if (remoteAudio.current) remoteAudio.current.srcObject = remote;
  }, [remote]);

  useEffect(() => {
    if (localVideo.current) localVideo.current.srcObject = local;
  }, [local]);

  if (phase === 'idle' || phase === 'ended') return null;

  if (phase === 'incoming') {
    return (
      <div className="incoming-call" role="dialog" aria-label="Входящий звонок">
        <header>
          <Avatar name={peerName} />
          <div>
            <strong>{peerName}</strong>
            <br />
            <small>{mediaKind === 'video' ? 'Видеозвонок' : 'Аудиозвонок'}</small>
          </div>
        </header>
        <div className="modal-actions">
          <button type="button" className="secondary-btn" onClick={session.reject}>
            <PhoneOff size={17} /> Отклонить
          </button>
          <button type="button" className="primary-btn" onClick={session.accept}>
            <Phone size={17} /> Ответить
          </button>
        </div>
      </div>
    );
  }

  const status =
    phase === 'ringing'
      ? 'Вызываем…'
      : phase === 'connecting'
        ? 'Соединяем…'
        : formatDuration(startedAt);
  void tick;

  return (
    <div className="call-overlay" role="dialog" aria-label="Звонок">
      <div className="call-top">
        <strong>{peerName}</strong>
        <span className="muted tiny">{mediaKind === 'video' ? 'Видеозвонок' : 'Аудиозвонок'}</span>
      </div>

      <div className="call-center">
        {mediaKind === 'video' && remoteStream ? (
          <>
            <video className="call-video" ref={remoteVideo} autoPlay playsInline muted>
              <track kind="captions" />
            </video>
            <video className="call-self-video" ref={localVideo} autoPlay playsInline muted>
              <track kind="captions" />
            </video>
          </>
        ) : (
          <>
            <span className="call-avatar">
              <Avatar name={peerName} size="large" />
            </span>
            <h2>{peerName}</h2>
          </>
        )}
        <p aria-live="polite">{status}</p>
        {/* Audio is always attached, even in a video call: if the camera track
            never arrives the conversation still works. */}
        <audio ref={remoteAudio} autoPlay />
      </div>

      <div className="call-controls">
        <button
          type="button"
          className={micEnabled ? 'call-control' : 'call-control active'}
          onClick={session.toggleMicrophone}
          aria-pressed={!micEnabled}
        >
          <span>{micEnabled ? <Mic size={22} /> : <MicOff size={22} />}</span>
          {micEnabled ? 'Микрофон' : 'Выключен'}
        </button>

        {mediaKind === 'video' ? (
          <button
            type="button"
            className={cameraEnabled ? 'call-control' : 'call-control active'}
            onClick={session.toggleCamera}
            aria-pressed={!cameraEnabled}
          >
            <span>{cameraEnabled ? <Video size={22} /> : <VideoOff size={22} />}</span>
            {cameraEnabled ? 'Камера' : 'Выключена'}
          </button>
        ) : null}

        <button type="button" className="call-control end" onClick={session.hangUp}>
          <span>
            <PhoneOff size={22} />
          </span>
          Завершить
        </button>
      </div>
    </div>
  );
}
